import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import { trackersAPI, employeesAPI } from "../api/client";
import { isPrivilegedRole, normalizeRole } from "../utils/accessControl.js";
import { mayPerform, mayPerformWithRole } from "../utils/userAccess.js";
import "./Variabletracker.css";

const BONUS_TYPES = [
  "Billable bonus/Incentive", "Variable pay", "Retention bonus",
  "Joining bonus", "Milestone achievement bonus", "Recruitment incentive",
  "Project performance bonus", "Additional project incentive",
];
const FREQUENCY_OPTIONS = ["Monthly", "Quarterly", "Half yearly", "Yearly", "Milestone linked"];
const BASIS_OPTIONS = ["CTC annexure", "Policy", "Adhoc"];
const BUSINESS_UNIT_OPTIONS = ["IT", "HR", "Finance"];
const DIVISION_OPTIONS = ["Software", "Recruitment", "Accounting"];
const APPROVAL_STATUSES = ["Draft", "Pending", "Submitted", "Approved"];
const PAYMENT_STATUSES = ["Pending", "Paid", "Cancelled"];

function getPayableMonthOptions() {
  const year = new Date().getFullYear();
  const allMonths = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  return allMonths.map((m) => `${m} ${year}`);
}

function getPaymentMonthOptions() {
  const year = new Date().getFullYear();
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  return months.map((m) => `${m} ${year}`);
}

const initialFormData = {
  employeeNumber: "", name: "", doj: "", mobileNo: "", ptDate: "",
  businessUnit: "", division: "", department: "", customerName: "", location: "",
  bonusType: "", frequency: "", basis: "", amount: "",
  annualizedAmount: "", payableMonthYear: "",
  approver: "", approverEmail: "", paymentTeamEmail: "",
  approvalStatus: "Draft", paymentStatus: "Pending", paymentMonthYear: "",
  remarks: "",
};

function getCurrentUserRole() {
  try {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode(token);
      return { role: decoded?.role || "unknown", email: (decoded?.email || "").toLowerCase() };
    }
  } catch { /* ignore */ }
  return { role: "unknown", email: "" };
}

function computeAnnualizedAmount(value, frequency) {
  const amount = parseFloat(value);
  if (!amount || amount <= 0) return "0";
  switch (frequency) {
    case "Monthly": return String(amount * 12);
    case "Quarterly": return String(amount * 4);
    case "Half yearly": return String(amount * 2);
    case "Yearly":
    case "Milestone linked": return String(amount);
    default: return String(amount);
  }
}

export default function VariableTracker() {
  const [variableData, setVariableData] = useState([]);
  const [activeView, setActiveView] = useState("variable");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter1, setFilter1] = useState("");
  const [filter2, setFilter2] = useState("");
  const [filter3, setFilter3] = useState("");
  const [filter4, setFilter4] = useState("");
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [formData, setFormData] = useState(initialFormData);
  const [lastInfo, setLastInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [duplicateWarning, setDuplicateWarning] = useState("");
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [empStatusFilter, setEmpStatusFilter] = useState("");
  const employeeDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const [businessUnitOptions, setBusinessUnitOptions] = useState(BUSINESS_UNIT_OPTIONS);
  const [divisionOptions, setDivisionOptions] = useState(DIVISION_OPTIONS);
  const [payableMonths, setPayableMonths] = useState([]);
  const [showPayableMonthDropdown, setShowPayableMonthDropdown] = useState(false);
  const payableMonthDropdownRef = useRef(null);
  const [approverOptions, setApproverOptions] = useState([]);
  const [approverSearch, setApproverSearch] = useState("");
  const [showApproverDropdown, setShowApproverDropdown] = useState(false);
  const [approverStatusFilter, setApproverStatusFilter] = useState("");
  const approverDropdownRef = useRef(null);
  const [page, setPage] = useState(1);
  const rowsPerPage = 20;
  const formSnapshotRef = useRef(null);

  useEffect(() => {
    const closeExportDropdownOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        exportDropdownRef.current.open = false;
      }
    };
    document.addEventListener("mousedown", closeExportDropdownOutside);
    return () => document.removeEventListener("mousedown", closeExportDropdownOutside);
  }, []);

  const { role: userRole, email: userEmail } = getCurrentUserRole();
  const isPrivilegedEditor = useMemo(() => isPrivilegedRole(userRole), [userRole]);
  const canEdit = useMemo(() => {
    const normalized = normalizeRole(userRole);
    return (isPrivilegedEditor || normalized === "admin_user") && mayPerform("variable", "edit");
  }, [userRole, isPrivilegedEditor]);
  const canExport = mayPerformWithRole(userRole, "variable", "export", "variable.export");

  const isFormDirty = useCallback(() => {
    if (!formSnapshotRef.current) return false;
    return JSON.stringify(formData) !== JSON.stringify(formSnapshotRef.current);
  }, [formData]);

  const confirmClose = useCallback(() => {
    if (isFormDirty() && !window.confirm("You have unsaved changes. Are you sure you want to close?")) return;
    setShowForm(false);
    resetForm();
    formSnapshotRef.current = null;
  }, [isFormDirty]);

  const annualizedAmount = useMemo(
    () => computeAnnualizedAmount(formData.amount, formData.frequency),
    [formData.amount, formData.frequency]
  );

  const loadVariableEntries = async () => {
    setError("");
    setLoading(true);
    try {
      const response = await trackersAPI.getVariableEntries();
      if (Array.isArray(response.data)) {
        setVariableData(response.data);
      }
    } catch (err) {
      console.error("Failed to load variable tracker entries", err);
      setError("Unable to load variable tracker records from server.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadVariableEntries(); }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await employeesAPI.getFilterValues();
        const data = res.data || {};
        if (Array.isArray(data.business_units) && data.business_units.length > 0) {
          setBusinessUnitOptions([...new Set(data.business_units.map((b) => String(b)).filter((b) => b.trim() !== ""))]);
        }
        if (Array.isArray(data.divisions) && data.divisions.length > 0) {
          setDivisionOptions([...new Set(data.divisions.map((d) => String(d)).filter((d) => d.trim() !== ""))]);
        }
      } catch (err) {
        console.error("Failed to fetch employee filter values", err);
      }
    })();
  }, []);

  const loadEmployeeOptions = useCallback(async (search, status = empStatusFilter) => {
    if (!search || search.length < 1) {
      setEmployeeOptions([]);
      return;
    }
    try {
      const params = { limit: 50, search };
      if (status) params.status = status;
      const response = await employeesAPI.getAll(params);
      const employees = Array.isArray(response.data?.employees) ? response.data.employees : [];
      setEmployeeOptions(employees);
    } catch (err) {
      console.error("Failed to load employee list", err);
      setEmployeeOptions([]);
    }
  }, [empStatusFilter]);

  useEffect(() => {
    if (!showEmployeeDropdown) return;
    const timer = setTimeout(() => { loadEmployeeOptions(employeeSearch); }, 300);
    return () => clearTimeout(timer);
  }, [employeeSearch, loadEmployeeOptions, showEmployeeDropdown]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(e.target)) {
        setShowEmployeeDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (payableMonthDropdownRef.current && !payableMonthDropdownRef.current.contains(e.target)) {
        setShowPayableMonthDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadApproverOptions = useCallback(async (search, status = approverStatusFilter) => {
    if (!search || search.length < 1) {
      setApproverOptions([]);
      return;
    }
    try {
      const params = { limit: 50, search };
      if (status) params.status = status;
      const response = await employeesAPI.getAll(params);
      const employees = Array.isArray(response.data?.employees) ? response.data.employees : [];
      setApproverOptions(employees);
    } catch (err) {
      console.error("Failed to load approver list", err);
      setApproverOptions([]);
    }
  }, [approverStatusFilter]);

  useEffect(() => {
    if (!showApproverDropdown) return;
    const timer = setTimeout(() => { loadApproverOptions(approverSearch); }, 300);
    return () => clearTimeout(timer);
  }, [approverSearch, loadApproverOptions, showApproverDropdown]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (approverDropdownRef.current && !approverDropdownRef.current.contains(e.target)) {
        setShowApproverDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (showForm && isFormDirty()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [showForm, isFormDirty]);

  const selectApprover = (emp) => {
    if (formData.employeeNumber && String(emp.employee_id) === String(formData.employeeNumber)) {
      setError("You cannot assign yourself as the approver. Please select a different employee.");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      approver: emp.name || "",
      approverEmail: emp.email || "",
    }));
    setShowApproverDropdown(false);
    setApproverSearch("");
    setApproverOptions([]);
  };

  const handleApproverSearchChange = (e) => {
    const val = e.target.value;
    setApproverSearch(val);
    setFormData((prev) => ({ ...prev, approver: val, approverEmail: "" }));
    setShowApproverDropdown(true);
  };

  const selectEmployee = (emp) => {
    setFormData((prev) => ({
      ...prev,
      employeeNumber: emp.employee_id || "",
      name: emp.name || "",
      doj: emp.joining_date ? String(emp.joining_date).substring(0, 10) : "",
      mobileNo: "",
      businessUnit: emp.business_unit || "",
      division: emp.division || "",
      department: emp.department || "",
      location: emp.seating_location || "",
    }));
    setShowEmployeeDropdown(false);
    setEmployeeSearch("");
    setEmployeeOptions([]);
  };

  const handleEmployeeSearchChange = (e) => {
    const val = e.target.value;
    setEmployeeSearch(val);
    setFormData((prev) => ({ ...prev, employeeNumber: val }));
    setShowEmployeeDropdown(true);
  };

  const checkDuplicate = useCallback(async (empNum, bonusType, payableMonth) => {
    if (!empNum || !bonusType || !payableMonth) {
      setDuplicateWarning("");
      return false;
    }
    const months = payableMonth.split(", ").filter(Boolean);
    try {
      for (const month of months) {
        const params = { employeeNumber: empNum, bonusType, payableMonthYear: month };
        if (editingId) params.excludeId = editingId;
        const res = await trackersAPI.checkDuplicateVariable(params);
        if (res.data?.duplicate) {
          setDuplicateWarning(`Warning: A similar incentive record already exists for this employee for ${month} (${res.data.count} duplicate(s) found).`);
          return true;
        }
      }
      setDuplicateWarning("");
      return false;
    } catch {
      setDuplicateWarning("");
      return false;
    }
  }, [editingId]);

  const filteredData = useMemo(() => {
    return variableData.filter((item) => {
      const matchesSearch = [
        item.employeeNumber, item.name, item.businessUnit, item.division,
        item.department, item.customerName, item.bonusType, item.approver,
      ].join(" ").toLowerCase().includes(searchTerm.toLowerCase());

      const matchesFilter =
        (!filter1 || item.businessUnit === filter1) &&
        (!filter2 || item.division === filter2) &&
        (!filter3 || item.approvalStatus === filter3) &&
        (!filter4 || item.paymentStatus === filter4);

      return matchesSearch && matchesFilter;
    });
  }, [variableData, searchTerm, filter1, filter2, filter3, filter4]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage) || 1;
  const pageData = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filter1, filter2, filter3, filter4]);

  const handleClearFilters = () => {
    setSearchTerm("");
    setFilter1("");
    setFilter2("");
    setFilter3("");
    setFilter4("");
  };

  const pendingApprovals = useMemo(() => {
    if (!userEmail) return [];
    const isFullAccess = isPrivilegedRole(userRole);
    return variableData.filter((item) => {
      const isPending = item.approvalStatus === "Pending" || item.approvalStatus === "Submitted";
      if (isFullAccess) return isPending;
      const isMyApproval = (item.approverEmail || "").toLowerCase() === userEmail;
      return isPending && isMyApproval;
    });
  }, [variableData, userEmail, userRole]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const togglePayableMonth = (month) => {
    setPayableMonths((prev) => {
      const next = prev.includes(month) ? prev.filter((m) => m !== month) : [...prev, month];
      setFormData((p) => ({ ...p, payableMonthYear: next.join(", ") }));
      return next;
    });
  };

  const selectAllPayableMonths = () => {
    const all = getPayableMonthOptions();
    setPayableMonths(all);
    setFormData((p) => ({ ...p, payableMonthYear: all.join(", ") }));
  };

  const clearAllPayableMonths = () => {
    setPayableMonths([]);
    setFormData((p) => ({ ...p, payableMonthYear: "" }));
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData(initialFormData);
    setPayableMonths([]);
    setApproverSearch("");
    setApproverOptions([]);
    setShowApproverDropdown(false);
    setDuplicateWarning("");
    setError("");
  };

  const openForm = () => { resetForm(); setShowForm(true); setLastInfo(""); formSnapshotRef.current = JSON.parse(JSON.stringify(initialFormData)); };
  const closeForm = () => { setShowForm(false); resetForm(); };
  const handleAddNew = () => { if (!canEdit) return; openForm(); };

  const saveEntry = async (submitted = false) => {
    if (!canEdit) return;

    if (!formData.employeeNumber || !formData.name) {
      setError("Employee Number and Name are required.");
      return;
    }
    if (!formData.bonusType) {
      setError("Bonus/Incentive Type is required.");
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError("Amount is required and must be greater than 0.");
      return;
    }
    if (!formData.payableMonthYear) {
      setError("Please select at least one Payable Month.");
      return;
    }
    if (!formData.approver || !formData.approverEmail) {
      setError("Please select an Approver from the employee list.");
      return;
    }

    if (submitted && formData.approverEmail === userEmail) {
      setError("You cannot submit a request to yourself for approval. Please select a different approver.");
      return;
    }

    const isDup = await checkDuplicate(formData.employeeNumber, formData.bonusType, formData.payableMonthYear);
    if (isDup && submitted) {
      if (!window.confirm("Duplicate incentive detected. Do you want to proceed with submission?")) {
        return;
      }
    }

    let newApprovalStatus;
    if (editingId) {
      const existing = variableData.find((r) => r.id === editingId);
      const currentStatus = existing?.approvalStatus || formData.approvalStatus;
      if (submitted && currentStatus === "Draft") {
        newApprovalStatus = "Pending";
      } else if (currentStatus === "Approved") {
        newApprovalStatus = currentStatus;
      } else {
        newApprovalStatus = formData.approvalStatus;
      }
    } else {
      newApprovalStatus = submitted ? "Pending" : "Draft";
    }

    const newEntry = {
      employeeId: null,
      employeeNumber: formData.employeeNumber,
      name: formData.name,
      doj: formData.doj,
      mobileNo: formData.mobileNo,
      ptDate: formData.ptDate,
      businessUnit: formData.businessUnit,
      division: formData.division,
      department: formData.department,
      customerName: formData.customerName,
      location: formData.location,
      bonusType: formData.bonusType,
      frequency: formData.frequency,
      basis: formData.basis,
      amount: parseFloat(formData.amount) || 0,
      payableMonthYear: formData.payableMonthYear,
      approver: formData.approver,
      approverEmail: formData.approverEmail,
      paymentTeamEmail: formData.paymentTeamEmail,
      approvalStatus: newApprovalStatus,
      paymentStatus: formData.paymentStatus,
      paymentMonthYear: formData.paymentMonthYear,
      remarks: formData.remarks,
    };

    setLoading(true);
    setError("");

    try {
      let savedEntry = { ...newEntry, annualizedAmount: parseFloat(annualizedAmount) || 0 };

      if (editingId) {
        await trackersAPI.updateVariableEntry(editingId, savedEntry);
        savedEntry = { ...savedEntry, id: editingId };
      } else {
        const response = await trackersAPI.createVariableEntry(savedEntry);
        savedEntry = { ...savedEntry, id: response.data?.id || Date.now() };
      }

      formSnapshotRef.current = null;
      closeForm();
      setLastInfo(submitted ? "Sent for approval." : "Saved as draft.");
      await loadVariableEntries();
    } catch (err) {
      console.error("Failed to save variable tracker entry", err);
      setError(err.response?.data?.error || "Failed to save variable tracker entry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = () => saveEntry(false);
  const handleSendApproval = () => saveEntry(true);

  const handleEdit = (row) => {
    if (!canEdit) return;
    setEditingId(row.id);
    const months = row.payableMonthYear ? row.payableMonthYear.split(", ").filter(Boolean) : [];
    setPayableMonths(months);
    setApproverSearch(row.approver || "");
    setFormData({
      employeeNumber: row.employeeNumber,
      name: row.name,
      doj: row.doj || "",
      mobileNo: row.mobileNo || "",
      ptDate: row.ptDate || "",
      businessUnit: row.businessUnit || "",
      division: row.division || "",
      department: row.department || "",
      customerName: row.customerName || "",
      location: row.location || "",
      bonusType: row.bonusType || "",
      frequency: row.frequency || "",
      basis: row.basis || "",
      amount: String(row.amount || ""),
      annualizedAmount: String(row.annualizedAmount || ""),
      payableMonthYear: row.payableMonthYear || "",
      approver: row.approver || "",
      approverEmail: row.approverEmail || "",
      paymentTeamEmail: row.paymentTeamEmail || "",
      approvalStatus: row.approvalStatus || "Draft",
      paymentStatus: row.paymentStatus || "Pending",
      paymentMonthYear: row.paymentMonthYear || "",
      remarks: row.remarks || "",
    });
    formSnapshotRef.current = JSON.parse(JSON.stringify({
      employeeNumber: row.employeeNumber,
      name: row.name,
      doj: row.doj || "",
      mobileNo: row.mobileNo || "",
      ptDate: row.ptDate || "",
      businessUnit: row.businessUnit || "",
      division: row.division || "",
      department: row.department || "",
      customerName: row.customerName || "",
      location: row.location || "",
      bonusType: row.bonusType || "",
      frequency: row.frequency || "",
      basis: row.basis || "",
      amount: String(row.amount || ""),
      annualizedAmount: String(row.annualizedAmount || ""),
      payableMonthYear: row.payableMonthYear || "",
      approver: row.approver || "",
      approverEmail: row.approverEmail || "",
      paymentTeamEmail: row.paymentTeamEmail || "",
      approvalStatus: row.approvalStatus || "Draft",
      paymentStatus: row.paymentStatus || "Pending",
      paymentMonthYear: row.paymentMonthYear || "",
      remarks: row.remarks || "",
    }));
    setShowForm(true);
    setLastInfo("");
  };

  const handleSelectRow = (id, checked) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleApproveSelected = async () => {
    if (!canEdit || selectedRows.size === 0) return;
    setLoading(true);
    setError("");

    try {
      await trackersAPI.approveVariableEntries([...selectedRows]);
      setVariableData((prev) =>
        prev.map((item) =>
          selectedRows.has(item.id) && item.approvalStatus === "Pending"
            ? { ...item, approvalStatus: "Approved", approvedBy: userRole }
            : item
        )
      );
      setLastInfo("Selected records have been approved.");
    } catch (err) {
      console.error("Failed to approve selected rows", err);
      setError("Unable to approve selected records. Please try again.");
    } finally {
      setLoading(false);
      setSelectedRows(new Set());
    }
  };

  const handlePaymentUpdate = async () => {
    if (!isPaymentTeamEditor || !editingId) return;
    setLoading(true);
    setError("");
    try {
      await trackersAPI.updatePaymentStatus(editingId, {
        paymentStatus: formData.paymentStatus,
        paymentMonthYear: formData.paymentMonthYear,
      });
      setLastInfo("Payment status updated successfully.");
      formSnapshotRef.current = null;
      closeForm();
      await loadVariableEntries();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to update payment status.");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const res = await trackersAPI.exportTracker("variable", format);
      const isCsv = format === "csv";
      const blob = new Blob([res.data], { type: isCsv ? "text/csv;charset=utf-8" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `variable_tracker_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setLastInfo("Export downloaded successfully.");
    } catch (err) {
      console.error("Export failed", err);
      setError("Failed to export data.");
    }
  };

  const isFieldLocked = (fieldName) => {
    if (!canEdit) return true;
    // Highest-access roles (Developer, Developer Head, Super Admin) can edit
    // any record — only the approval status itself stays read-only.
    if (isPrivilegedEditor) {
      return fieldName === "approvalStatus";
    }
    if (fieldName === "approvalStatus") return true;
    if (fieldName === "paymentStatus" || fieldName === "paymentMonthYear") {
      if (editingId) {
        const existing = variableData.find((r) => r.id === editingId);
        if (existing && existing.approvalStatus === "Approved" && userEmail && (existing.paymentTeamEmail || "").toLowerCase() === userEmail) {
          return false;
        }
      }
      return true;
    }
    if (editingId) {
      const existing = variableData.find((r) => r.id === editingId);
      if (existing && existing.approvalStatus !== "Draft") {
        return true;
      }
    }
    return false;
  };

  const isPaymentTeamEditor = useMemo(() => {
    if (!editingId || !userEmail) return false;
    const existing = variableData.find((r) => r.id === editingId);
    return existing && existing.approvalStatus === "Approved" && (existing.paymentTeamEmail || "").toLowerCase() === userEmail;
  }, [editingId, userEmail, variableData]);

  const editingRecord = editingId ? variableData.find((r) => r.id === editingId) : null;
  const isEditingNonDraft = !isPrivilegedEditor && editingRecord && editingRecord.approvalStatus !== "Draft";

  const renderVariablePayTable = () => (
    <div className="tracker-card">
        <div className="tracker-card-header">
          <div className="tracker-card-title">
            <h2>Variable Pay Records</h2>
            <span className="tracker-count">{filteredData.length} record{filteredData.length !== 1 ? "s" : ""}</span>
          </div>
        </div>

      <div className="tracker-filters">
        <div className="filter-group">
          <label>Search</label>
          <input type="text" placeholder="Search by Emp No, Name, Dept..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="filter-input" />
        </div>
        <div className="filter-group">
          <label>Business Unit</label>
          <select value={filter1} onChange={(e) => setFilter1(e.target.value)} className="filter-input">
            <option value="">All</option>
            {businessUnitOptions.map((b) => (<option key={b} value={b}>{b}</option>))}
          </select>
        </div>
        <div className="filter-group">
          <label>Division</label>
          <select value={filter2} onChange={(e) => setFilter2(e.target.value)} className="filter-input">
            <option value="">All</option>
            {divisionOptions.map((d) => (<option key={d} value={d}>{d}</option>))}
          </select>
        </div>
        <div className="filter-group">
          <label>Approval Status</label>
          <select value={filter3} onChange={(e) => setFilter3(e.target.value)} className="filter-input">
            <option value="">All</option>
            {APPROVAL_STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
          </select>
        </div>
        <div className="filter-group">
          <label>Payment Status</label>
          <select value={filter4} onChange={(e) => setFilter4(e.target.value)} className="filter-input">
            <option value="">All</option>
            {PAYMENT_STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
          </select>
      </div>
        <button type="button" className="btn btn-clear-filters" onClick={handleClearFilters}>
          Clear Filters
        </button>
    </div>

      <div className="tracker-table-wrapper">
        <table className="tracker-table">
          <thead>
            <tr>
              <th scope="col">Emp No</th>
              <th scope="col">Name</th>
              <th scope="col">Bonus Type</th>
              <th scope="col">Frequency</th>
              <th scope="col">Amount</th>
              <th scope="col">Approver</th>
              <th scope="col">Status</th>
              <th scope="col">Payment</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {pageData.length > 0 ? (
              pageData.map((row) => (
                <tr key={row.id}>
                  <td className="td-emp">{row.employeeNumber}</td>
                  <td className="td-name">{row.name}</td>
                  <td>{row.bonusType}</td>
                  <td>{row.frequency}</td>
                  <td className="td-amount">₹{Number(row.amount || 0).toLocaleString()}</td>
                  <td>{row.approver || "-"}</td>
                  <td>
                    <span className={`status-chip status-${row.approvalStatus.replace(/\s+/g, "-").toLowerCase()}`}>
                      {row.approvalStatus}
                    </span>
                  </td>
                  <td>
                    <span className={`status-chip payment-${row.paymentStatus.toLowerCase()}`}>
                      {row.paymentStatus}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {(row.approvalStatus === "Draft" || isPrivilegedEditor) ? (
                        <button className="btn-action" onClick={() => handleEdit(row)}>Edit</button>
                      ) : (
                        <button className="btn-action btn-view" onClick={() => handleEdit(row)}>View</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="no-data">No variable pay records found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="pagination">
          <button className="btn btn-pagination" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>&#9664; Prev</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button key={p} className={`btn btn-pagination ${p === page ? "btn-pagination-active" : ""}`} onClick={() => setPage(p)}>{p}</button>
          ))}
          <button className="btn btn-pagination" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next &#9654;</button>
          <span className="pagination-info">{filteredData.length} records</span>
        </div>
      )}
    </div>
  );

  const renderApprovalsTable = () => (
    <div className="tracker-card">
      <div className="tracker-card-header">
        <div className="tracker-card-title">
          <h2>Pending Approvals</h2>
          <span className="tracker-count">{pendingApprovals.length} record{pendingApprovals.length !== 1 ? "s" : ""} awaiting your review</span>
        </div>
        {canEdit && (
          <div className="tracker-card-actions">
            <button className="btn btn-primary" onClick={handleApproveSelected} disabled={selectedRows.size === 0}>
              Approve Selected ({selectedRows.size})
            </button>
          </div>
        )}
      </div>

      {pendingApprovals.length > 0 ? (
        <div className="tracker-table-wrapper">
          <table className="tracker-table approval-table">
            <thead>
              <tr>
                <th scope="col" className="th-select">
                  <input
                    type="checkbox"
                    checked={selectedRows.size === pendingApprovals.length && pendingApprovals.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRows(new Set(pendingApprovals.map((r) => r.id)));
                      } else {
                        setSelectedRows(new Set());
                      }
                    }}
                  />
                </th>
                <th scope="col">Emp No</th>
                <th scope="col">Name</th>
                <th scope="col">Bonus Type</th>
                <th scope="col">Frequency</th>
                <th scope="col">Amount</th>
                <th scope="col">Submitted By</th>
                <th scope="col">Status</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {pendingApprovals.map((row) => (
                <tr key={row.id} className="approval-row">
                  <td className="td-select">
                    <input
                      type="checkbox"
                      checked={selectedRows.has(row.id)}
                      onChange={(e) => handleSelectRow(row.id, e.target.checked)}
                    />
                  </td>
                  <td className="td-emp">{row.employeeNumber}</td>
                  <td className="td-name">{row.name}</td>
                  <td>{row.bonusType}</td>
                  <td>{row.frequency}</td>
                  <td className="td-amount">₹{Number(row.amount || 0).toLocaleString()}</td>
                  <td>{row.name}</td>
                  <td>
                    <span className={`status-chip status-${row.approvalStatus.replace(/\s+/g, "-").toLowerCase()}`}>
                      {row.approvalStatus}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-action btn-view" onClick={() => handleEdit(row)}>View</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-approval-state">
          <div className="empty-icon">✓</div>
          <h3>All caught up!</h3>
          <p>No pending approvals assigned to you right now.</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="page variable-tracker">
      <div className="tracker-container">
        <div className="tracker-topbar">
          <div className="tracker-title-section">
            <h1>Variable Pay & Incentives</h1>
            <p className="tracker-subtitle">Manage bonus and incentive records for active employees</p>
          </div>
          <div className="tracker-top-actions">
            {canEdit && activeView === "variable" && (
              <button className="btn btn-primary" onClick={handleAddNew}>+ Add New</button>
            )}
            {activeView === "variable" && (
              <details className="tracker-export-dropdown" ref={exportDropdownRef}>
                <summary className="btn btn-secondary" onClick={(event) => { if (!canExport) event.preventDefault(); }}>Export ▾</summary>
                <div className="tracker-export-menu" role="menu">
                  <button type="button" role="menuitem" disabled={!canExport} onClick={(event) => { event.currentTarget.closest("details").open = false; handleExport("xlsx"); }}>Excel</button>
                  <button type="button" role="menuitem" disabled={!canExport} onClick={(event) => { event.currentTarget.closest("details").open = false; handleExport("csv"); }}>CSV</button>
                </div>
              </details>
            )}
          </div>
        </div>

        <div className="tracker-tabs">
          <button
            className={`tracker-tab ${activeView === "variable" ? "active" : ""}`}
            onClick={() => { setActiveView("variable"); setSelectedRows(new Set()); }}
          >
            Variable Pay
            {activeView !== "variable" && filteredData.length > 0 && (
              <span className="tab-count">{filteredData.length}</span>
            )}
          </button>
          <button
            className={`tracker-tab ${activeView === "approvals" ? "active" : ""}`}
            onClick={() => { setActiveView("approvals"); setSelectedRows(new Set()); }}
          >
            My Approvals
            {pendingApprovals.length > 0 && (
              <span className="tab-count tab-count-pending">{pendingApprovals.length}</span>
            )}
          </button>
        </div>

        {error && <div className="error-banner">{error}</div>}
        {loading && <div className="loading-banner">Loading…</div>}

        {activeView === "variable" && renderVariablePayTable()}
        {activeView === "approvals" && renderApprovalsTable()}

        {lastInfo && <div className="info-banner">{lastInfo}</div>}

        {showForm && (
          <div className="modal-overlay" onClick={confirmClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingId ? (isEditingNonDraft ? "View" : "Edit") : "Add"} Incentive Record</h2>
                <button className="close-btn" onClick={confirmClose}>✕</button>
              </div>

              <div className="modal-body">
                {isEditingNonDraft && (
                  <div className="info-banner">
                    {isPaymentTeamEditor
                      ? `This record is "${editingRecord.approvalStatus}". You can update Payment Status and Payment Month.`
                      : `This record is in "${editingRecord.approvalStatus}" status. Fields are read-only.`}
                  </div>
                )}

                <div className="form-section" ref={employeeDropdownRef} style={{ position: "relative" }}>
                  <label className="employee-number-label">Employee Number *</label>
                  <input
                    type="text"
                    name="employeeNumber"
                    value={formData.employeeNumber}
                    onChange={handleEmployeeSearchChange}
                    onFocus={() => { if (formData.employeeNumber && !editingId) { setShowEmployeeDropdown(true); loadEmployeeOptions(formData.employeeNumber); } }}
                    placeholder="Type employee number or name to search..."
                    className="input-employee-number"
                    disabled={isFieldLocked("employeeNumber")}
                    autoComplete="off"
                  />
                  <p className="form-instruction">Type employee number or name to search and auto-populate from Zoho People.</p>
                  {showEmployeeDropdown && (
                    <div className="employee-dropdown">
                      <div className="employee-dropdown-filters">
                        <span className="employee-dropdown-filters-label">Status</span>
                        <button type="button" className={`employee-dropdown-filter-btn ${empStatusFilter === '' ? 'active' : ''}`} onClick={() => setEmpStatusFilter('')}>All</button>
                        <button type="button" className={`employee-dropdown-filter-btn ${empStatusFilter === 'active' ? 'active' : ''}`} onClick={() => setEmpStatusFilter('active')}>Active</button>
                        <button type="button" className={`employee-dropdown-filter-btn ${empStatusFilter === 'inactive' ? 'inactive' : ''}`} onClick={() => setEmpStatusFilter('inactive')}>Inactive</button>
                      </div>
                      {employeeOptions.length > 0 ? (
                        employeeOptions.map((emp) => (
                          <div key={emp.employee_id} className="employee-dropdown-item" onClick={() => selectEmployee(emp)}>
                            <span>
                              <strong>{emp.employee_id}</strong> — {emp.name}
                              {emp.department ? ` (${emp.department})` : ""}
                            </span>
                            {emp.status ? <span className="emp-status-tag">{emp.status}</span> : null}
                          </div>
                        ))
                      ) : (
                        <div className="employee-dropdown-empty">No employees found.</div>
                      )}
                    </div>
                  )}
                </div>

                {duplicateWarning && <div className="warning-banner">{duplicateWarning}</div>}

                <div className="form-grid">
                  <div className="form-group">
                    <label>Name *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleFormChange} className="input-field" disabled={isFieldLocked("name")} />
                  </div>
                  <div className="form-group">
                    <label>DOJ</label>
                    <input type="date" name="doj" value={formData.doj} onChange={handleFormChange} className="input-field" disabled={isFieldLocked("doj")} />
                  </div>
                  <div className="form-group">
                    <label>Mobile No</label>
                    <input type="text" name="mobileNo" value={formData.mobileNo} onChange={handleFormChange} className="input-field" disabled={isFieldLocked("mobileNo")} />
                  </div>
                  <div className="form-group">
                    <label>Business Unit</label>
                    <select name="businessUnit" value={formData.businessUnit} onChange={handleFormChange} className="input-field" disabled={isFieldLocked("businessUnit")}>
                      <option value="">Select</option>
                      {businessUnitOptions.map((b) => (<option key={b} value={b}>{b}</option>))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Division</label>
                    <select name="division" value={formData.division} onChange={handleFormChange} className="input-field" disabled={isFieldLocked("division")}>
                      <option value="">Select</option>
                      {divisionOptions.map((d) => (<option key={d} value={d}>{d}</option>))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Department</label>
                    <input type="text" name="department" value={formData.department} onChange={handleFormChange} className="input-field" disabled={isFieldLocked("department")} />
                  </div>
                  <div className="form-group">
                    <label>Customer Name</label>
                    <input type="text" name="customerName" value={formData.customerName} onChange={handleFormChange} className="input-field" disabled={isFieldLocked("customerName")} />
                  </div>
                  <div className="form-group">
                    <label>Location</label>
                    <input type="text" name="location" value={formData.location} onChange={handleFormChange} className="input-field" disabled={isFieldLocked("location")} />
                  </div>
                </div>

                <div className="form-section-divider">
                  <span className="divider-label">Incentive Details</span>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label>Bonus/Incentive Type *</label>
                    <select name="bonusType" value={formData.bonusType} onChange={handleFormChange} className="input-field" disabled={isFieldLocked("bonusType")}>
                      <option value="">Select type</option>
                      {BONUS_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Basis *</label>
                    <select name="basis" value={formData.basis} onChange={handleFormChange} className="input-field" disabled={isFieldLocked("basis")}>
                      <option value="">Select basis</option>
                      {BASIS_OPTIONS.map((b) => (<option key={b} value={b}>{b}</option>))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Frequency *</label>
                    <select name="frequency" value={formData.frequency} onChange={handleFormChange} className="input-field" disabled={isFieldLocked("frequency")}>
                      <option value="">Select frequency</option>
                      {FREQUENCY_OPTIONS.map((f) => (<option key={f} value={f}>{f}</option>))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Amount *</label>
                    <input type="number" name="amount" min="0" value={formData.amount} onChange={handleFormChange} className="input-field" placeholder="Enter amount" disabled={isFieldLocked("amount")} />
                  </div>
                  <div className="form-group">
                    <label>Annualized Amount</label>
                    <input type="text" value={`₹${Number(annualizedAmount || 0).toLocaleString()}`} className="input-field" disabled />
                  </div>
                  <div className="form-group" ref={payableMonthDropdownRef} style={{ position: "relative" }}>
                    <label>Payable Month & Year *</label>
                    <div
                      className="input-field multiselect-trigger"
                      onClick={() => !isFieldLocked("payableMonthYear") && setShowPayableMonthDropdown((p) => !p)}
                      style={{ cursor: isFieldLocked("payableMonthYear") ? "not-allowed" : "pointer" }}
                    >
                      {payableMonths.length > 0 ? `${payableMonths.length} month${payableMonths.length > 1 ? "s" : ""} selected` : "Choose months"}
                    </div>
                    {showPayableMonthDropdown && !isFieldLocked("payableMonthYear") && (
                      <div className="multiselect-dropdown">
                        <div className="multiselect-actions">
                          <button type="button" className="multiselect-action-btn" onClick={selectAllPayableMonths}>Select All</button>
                          <button type="button" className="multiselect-action-btn" onClick={clearAllPayableMonths}>Clear All</button>
                        </div>
                        <div className="multiselect-options">
                          {getPayableMonthOptions().map((m) => (
                            <label key={m} className="multiselect-option">
                              <input type="checkbox" checked={payableMonths.includes(m)} onChange={() => togglePayableMonth(m)} />
                              <span>{m}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-section-divider approver-divider">
                  <span className="divider-label">Approver Assignment</span>
                  <span className="divider-hint">Creator cannot be the approver</span>
                </div>

                <div className="form-grid">
                  <div className="form-group" ref={approverDropdownRef} style={{ position: "relative" }}>
                    <label>Select Approver *</label>
                    <input
                      type="text"
                      value={formData.approver}
                      onChange={handleApproverSearchChange}
                      onFocus={() => { if (!formData.approver) { setShowApproverDropdown(true); loadApproverOptions(""); } }}
                      placeholder="Type to search employee..."
                      className="input-field"
                      disabled={isFieldLocked("approver")}
                      autoComplete="off"
                    />
                    {showApproverDropdown && (
                      <div className="employee-dropdown">
                        <div className="employee-dropdown-filters">
                          <span className="employee-dropdown-filters-label">Status</span>
                          <button type="button" className={`employee-dropdown-filter-btn ${approverStatusFilter === '' ? 'active' : ''}`} onClick={() => setApproverStatusFilter('')}>All</button>
                          <button type="button" className={`employee-dropdown-filter-btn ${approverStatusFilter === 'active' ? 'active' : ''}`} onClick={() => setApproverStatusFilter('active')}>Active</button>
                          <button type="button" className={`employee-dropdown-filter-btn ${approverStatusFilter === 'inactive' ? 'inactive' : ''}`} onClick={() => setApproverStatusFilter('inactive')}>Inactive</button>
                        </div>
                        {approverOptions.length > 0 ? (
                          approverOptions.map((emp) => (
                            <div key={emp.employee_id} className="employee-dropdown-item" onClick={() => selectApprover(emp)}>
                              <span>
                                <strong>{emp.employee_id}</strong> — {emp.name}
                                {emp.email ? ` (${emp.email})` : ""}
                              </span>
                              {emp.status ? <span className="emp-status-tag">{emp.status}</span> : null}
                            </div>
                          ))
                        ) : (
                          <div className="employee-dropdown-empty">No employees found.</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Approver Email</label>
                    <input
                      type="email"
                      name="approverEmail"
                      value={formData.approverEmail}
                      className="input-field approver-email-field"
                      placeholder="Auto-filled when approver is selected"
                      disabled
                    />
                  </div>
                </div>

                <div className="form-section-divider">
                  <span className="divider-label">Payment & Status</span>
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label>Approval Status</label>
                    <input type="text" value={formData.approvalStatus} className="input-field" disabled />
                  </div>
                  <div className="form-group">
                    <label>Payment Status</label>
                    <select name="paymentStatus" value={formData.paymentStatus} onChange={handleFormChange} className="input-field" disabled={isFieldLocked("paymentStatus")}>
                      {PAYMENT_STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Payment Month & Year</label>
                    <select name="paymentMonthYear" value={formData.paymentMonthYear} onChange={handleFormChange} className="input-field" disabled={isFieldLocked("paymentMonthYear")}>
                      <option value="">Choose payment month</option>
                      {getPaymentMonthOptions().map((m) => (<option key={m} value={m}>{m}</option>))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Payment Team Email</label>
                    <input type="email" name="paymentTeamEmail" value={formData.paymentTeamEmail} onChange={handleFormChange} className="input-field" placeholder="payment@company.com" disabled={isFieldLocked("paymentTeamEmail")} />
                  </div>
                  <div className="form-group">
                    <label>Remarks</label>
                    <textarea name="remarks" value={formData.remarks} onChange={handleFormChange} className="input-field" placeholder="Free text" disabled={isFieldLocked("remarks")} />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                {canEdit && !isEditingNonDraft && (
                  <>
                    <button className="btn btn-save" disabled={loading} onClick={handleSaveDraft}>{loading ? "Saving..." : "Save Draft"}</button>
                    <button className="btn btn-submit" disabled={loading} onClick={handleSendApproval}>{loading ? "Saving..." : "Send for Approval"}</button>
                  </>
                )}
                {isPaymentTeamEditor && (
                  <button className="btn btn-primary" disabled={loading} onClick={handlePaymentUpdate}>{loading ? "Updating..." : "Update Payment"}</button>
                )}
                <button className="btn btn-cancel" onClick={confirmClose}>{isEditingNonDraft && !isPaymentTeamEditor ? "Close" : "Cancel"}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
